import { useCallback, useEffect, useState } from 'react';
import { comboboxKey } from '../lib/searchCombobox';

/**
 * ARIA combobox wiring for a text field and the listbox of results under it (lib/searchCombobox
 * has the key logic). `id` must be unique on the page: the desktop dropdown and the mobile
 * overlay each have their own.
 *
 * onSelect(i)  Enter on option i (a click on it is the option's own onClick)
 * onSubmit()   Enter with no option active
 * onClose()    Escape
 */
export function useSearchCombobox({ id, count, open, onSelect, onSubmit, onClose }) {
  const [active, setActive] = useState(-1);
  const listboxId = `${id}-listbox`;
  const optionId = useCallback((i) => `${id}-opcion-${i}`, [id]);

  // A new list (new results, reopened) starts with nothing active: Enter then means «search»,
  // not «open whatever is first».
  useEffect(() => { setActive(-1); }, [count, open]);

  // The active option stays in view as the arrows walk a list longer than the dropdown.
  useEffect(() => {
    if (active < 0) return;
    const el = document.getElementById(optionId(active));
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active, optionId]);

  const onKeyDown = (e) => {
    // Enter that confirms an IME composition is not a choice.
    if (e.key === 'Enter' && (e.isComposing || e.nativeEvent?.isComposing)) return;
    const r = comboboxKey(e.key, open ? active : -1, open ? count : 0);
    if (!r) return;
    e.preventDefault();
    setActive(r.active);
    if (r.action === 'select') onSelect(r.active);
    else if (r.action === 'submit') onSubmit?.();
    else if (r.action === 'close') onClose?.();
  };

  return {
    active,
    setActive,
    inputProps: {
      role: 'combobox',
      'aria-autocomplete': 'list',
      'aria-expanded': open,
      'aria-controls': listboxId,
      'aria-activedescendant': open && active >= 0 ? optionId(active) : undefined,
      onKeyDown,
    },
    listboxProps: { id: listboxId, role: 'listbox' },
    optionProps: (i) => ({
      id: optionId(i),
      role: 'option',
      'aria-selected': i === active,
      // The field keeps focus (and the list stays open) while an option is clicked.
      onMouseDown: (e) => e.preventDefault(),
      onMouseMove: () => { if (i !== active) setActive(i); },
    }),
  };
}
